import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProviderDetailsById,
  getProviderReviewsById,
  getSimilarProvidersById,
} from "../../api/details-page.api";
import {
  ProviderWithContactInfo,
  ProviderDetailsAccordion,
  FileInfo,
} from "./details-page.types";
import { ArrowBack } from "@mui/icons-material";
import CustomBreadcrumbs from "../../common/components/breadcrumb-component-new/breadcrumb-component-new";
import DetailsPageHeader from "./components/DetailsPageHeader";
import ProviderDescription from "./components/ProviderDescription";
import ProviderActionButtons from "./components/ProviderActionButtons";
import LocationComponent from "./components/LocationComponent";
import SimilarProvidersCarousel from "./components/SimilarProvidersCarousel";
import ImageGalleryComponent from "./components/image-gallery-component/image-gallery-component";
import ServicesAccordion from "./components/ServicesAccordion";
import MobileHeader from "../../common/components/mobile-header-component/MobileHeader";
import ReviewsComponent from "./components/ReviewsComponent";
import "./details-page.scss";

export default function DetailsPage() {
  // STATES
  const [providerDetails, setProviderDetails] =
    useState<ProviderDetailsAccordion | null | null>(null);

  const [similarProviders, setSimilarProviders] = useState<
    ProviderWithContactInfo[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [images, setImages] = useState<FileInfo[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const imageUrls = images.length > 0 ? images.map((img) => img.url) : [];

  // CONSTANTS
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const reviewsRef = useRef<HTMLDivElement>(null);

  const handleReviewsClick = () => {
    reviewsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleBack = () => {
    navigate("/home/list-of-funeral-homes/");
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const storedLocation = localStorage.getItem("user_location");
      let latitude = null;
      let longitude = null;

      if (storedLocation) {
        const loc = JSON.parse(storedLocation);
        latitude = loc.latitude;
        longitude = loc.longitude;
      }
      const [detailsResponse, similarResponse, reviewsResponse] =
        await Promise.all([
          getProviderDetailsById(Number(id), {
            userLat: latitude,
            userLng: longitude,
          }),
          getSimilarProvidersById(Number(id)),
          getProviderReviewsById(Number(id)),
        ]);

      if (detailsResponse.data.success) {
        const data = detailsResponse.data.data as ProviderDetailsAccordion;
        setProviderDetails(data);
        const filteredImages = data.fileInfo.filter(
          (file) => file.mediaType === "image",
        );
        setImages(filteredImages);
      }

      if (similarResponse.data.success) {
        setSimilarProviders(similarResponse.data.data.data || []);
      }
      if (reviewsResponse.data.success) {
        const reviewsData = reviewsResponse.data.data.data || [];
        setReviews(reviewsData);
      }
    } catch (error: any) {
      console.error("Error fetching details:", error);
      if (error?.response?.data.errorCode === "PARTNER_NOT_FOUND") {
        setErrorMessage("Provider is not serving in the selected location");
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const refreshReviewsData = async () => {
    try {
      // Only fetch details (for updated rating/count) and reviews
      const storedLocation = localStorage.getItem("user_location");
      let latitude = null;
      let longitude = null;

      if (storedLocation) {
        const loc = JSON.parse(storedLocation);
        latitude = loc.latitude;
        longitude = loc.longitude;
      }

      const [detailsResponse, reviewsResponse] = await Promise.all([
        getProviderDetailsById(Number(id), {
          userLat: latitude,
          userLng: longitude,
        }),
        getProviderReviewsById(Number(id)),
      ]);

      if (detailsResponse.data.success) {
        const data = detailsResponse.data.data as ProviderDetailsAccordion;
        setProviderDetails(data);
        const filteredImages = data.fileInfo.filter(
          (file) => file.mediaType === "image",
        );
        setImages(filteredImages);
      }

      if (reviewsResponse.data.success) {
        const reviewsData = reviewsResponse.data.data.data || [];
        setReviews(reviewsData);
      }
    } catch (error) {
      console.error("Error refreshing reviews:", error);
    }
  };

  const handleReviewSubmitted = () => {
    refreshReviewsData();
  };

  const handleReviewDeleted = () => {
    refreshReviewsData();
  };

  return (
    <>
      {providerDetails && (
        <>
          {isMobile && (
            <MobileHeader
              title="Details Page"
              onBack={() => navigate(-1)}
              className="custom-header"
              style={{ backgroundColor: "#f8f9fa" }}
            />
          )}
          <Container className="details-page">
            {!isMobile && (
              <Button
                startIcon={<ArrowBack fontSize="large" />}
                onClick={handleBack}
                sx={{
                  textTransform: "none",
                  color: "#161616",
                  paddingLeft: 0,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                  cursor: "pointer",
                }}
              >
                Back
              </Button>
            )}
            {!isMobile && (
              <CustomBreadcrumbs
                items={[
                  { label: "Home", to: "/home" },
                  { label: "List", to: "/home/list-of-funeral-homes" },
                  { label: "Detail page" },
                ]}
              />
            )}

            {/* Page Header */}
            <DetailsPageHeader providerDetails={providerDetails} />

            {/* Provider Description */}
            <ProviderDescription description={providerDetails.description} />

            {/* Action Buttons */}
            <ProviderActionButtons
              // ContactInfo={providerDetails.contactInfo}
              onReviewsClick={handleReviewsClick}
              email={providerDetails?.email}
              phoneNumber={providerDetails?.phoneNumber}
            />

            {/* Gallery & Map */}
            <Box className="gallery-map-location">
              <Box className="gallery-map-component ">
                <ImageGalleryComponent
                  images={imageUrls}
                  providerTitle={providerDetails.businessName}
                />
              </Box>
              <Box className="map-location-component">
                <LocationComponent
                  providerId={providerDetails.id}
                  type="provider"
                  locationInfo={providerDetails?.locationInfo}
                />
              </Box>
            </Box>

            {/* Accordion */}
            <ServicesAccordion providerDetails={providerDetails} />

            {/* Similar Providers */}
            <SimilarProvidersCarousel providers={similarProviders} />

            {/* Customer Reviews */}
            <Box ref={reviewsRef} className="customer-reviews">
              <ReviewsComponent
                reviews={reviews.slice(0, 3)}
                totalReviews={providerDetails.totalReviews}
                rating={providerDetails.avgRating}
                isMobile={isMobile}
                partnerId={providerDetails.id}
                onReviewSubmitted={handleReviewSubmitted}
                onReviewDeleted={handleReviewDeleted}
              />
              {reviews.length > 3 && (
                <Box display="flex" justifyContent="center" mt={2} mb={2}>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigate(
                        `/home/list-of-funeral-homes/details/${id}/reviews`,
                      )
                    }
                    sx={{
                      textTransform: "none",
                      borderColor: "rgba(16, 65, 69, 1)",
                      color: "rgba(16, 65, 69, 1)",
                      borderRadius: "8px",
                      width: "229px",
                      height: "48px",
                      fontSize: "16px",
                      fontWeight: 600,
                      fontFamily: "Roboto",
                    }}
                  >
                    View all reviews ({reviews.length - 3})
                  </Button>
                </Box>
              )}
              {reviews.length === 0 && (
                <Box textAlign="center" mt={4} mb={4}>
                  <p>No reviews available for this provider yet.</p>
                </Box>
              )}
            </Box>
          </Container>
        </>
      )}

      {loading && (
        <Box className="spinner-details-page">
          <CircularProgress
            size="3rem"
            className="spinner-details-page__spinner"
          />
        </Box>
      )}
      {errorMessage && (
        <Box className="spinner-details-page">{errorMessage}</Box>
      )}
    </>
  );
}
